import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { PageBanner } from '../components/Layout';
import Seo from '../components/Seo';
import { BLOG_POSTS, BLOG_CATEGORIES } from '../data/blogPosts';

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

const Blog = () => {
  const [cat, setCat] = useState('All Posts');

  const sorted = useMemo(
    () => [...BLOG_POSTS].sort((a, b) => (a.date < b.date ? 1 : -1)),
    []
  );
  const categories = useMemo(() => {
    const present = new Set(sorted.map((p) => p.category));
    return BLOG_CATEGORIES.filter((c) => c === 'All Posts' || present.has(c));
  }, [sorted]);

  const filtered = cat === 'All Posts' ? sorted : sorted.filter((p) => p.category === cat);

  return (
    <div>
      <Seo
        title="Blog — Insights on Sleep, Mood & Healing"
        description="Evidence-based articles on insomnia, CBT-I, anxiety, depression, trauma, ART and EMDR from Dr. Bill Hoekstra at The Mood & Sleep Clinic."
        path="/blog"
      />
      <PageBanner title="Blog" subtitle="Insights on sleep, mood, and healing" />

      <section className="py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {sorted.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-heading text-navy text-xl">New articles are on the way.</p>
              <p className="mt-2 text-muted-foreground">Please check back soon for insights on sleep, mood, and healing.</p>
            </div>
          ) : (
            <>
              {categories.length > 1 && (
                <div className="flex flex-wrap gap-2 justify-center mb-12">
                  {categories.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCat(c)}
                      className={`px-4 py-2 rounded-full text-sm font-heading transition-colors ${
                        cat === c ? 'bg-navy text-white' : 'bg-secondary text-navy/80 hover:bg-[#bfdcf0]'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {filtered.map((p) => (
                  <Link
                    to={`/blog/${p.slug}`}
                    key={p.slug}
                    className="group bg-white rounded-2xl border border-border overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col"
                  >
                    <div className="overflow-hidden h-48 bg-secondary">
                      {p.image ? (
                        <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#7aa6cf] font-heading text-sm px-4 text-center">The Mood &amp; Sleep Clinic</div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 text-xs font-heading font-semibold tracking-wide text-[#3d6a9c] uppercase">
                        <span>{p.category}</span>
                        {p.date && <><span className="text-border">•</span><span className="text-muted-foreground normal-case font-normal tracking-normal">{formatDate(p.date)}</span></>}
                      </div>
                      <h2 className="mt-2 font-heading text-lg text-navy leading-snug">{p.title}</h2>
                      <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{p.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-heading font-semibold text-navy group-hover:gap-2 transition-all">
                        Read more <ArrowUpRight size={16} />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
